import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { ErrorBox } from '@/components/commons/error-box'
import { Header } from '@/components/commons/header'

import { AlertCircleIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useGetExperience } from '@/features/experience-shell/queries/use-get-experience'
import { BackgroundStep } from '@/features/experience-view-only/components/background-step'
import { ExperienceStep } from '@/features/experience-view-only/components/experience-step'
import { LegalStep } from '@/features/experience-view-only/components/legal-step'
import { LocationStep } from '@/features/experience-view-only/components/location-step'
import { MerchandisingStep } from '@/features/experience-view-only/components/merchandising-step'
import { PhotosStep } from '@/features/experience-view-only/components/photos-step'
import { PricingDiscountStep } from '@/features/experience-view-only/components/pricing-discount-step'
import { StepsNavigation } from '@/features/experience-view-only/components/steps-navigation'
import { ExperienceStepKeyName } from '@/features/experience-view-only/types'
import { remapExperienceForViewOnly } from '@/features/experience-view-only/utils'
import { debounce, objectEntries, raise } from '@/helpers/utils'
import { useURLParsedParams } from '@/hooks/use-url-parsed-params'
import { useTranslation } from '@/translations/i18n'

export const ExperienceViewOnly = () => {
	const { t } = useTranslation()
	const params = useURLParsedParams(
		z.object({ id: z.coerce.number().int().positive() })
	)
	const experienceQuery = useGetExperience(
		params.success ? params.data.id : raise('id is nullish')
	)
	const experienceRef = useRef<HTMLDivElement>(null)
	const backgroundRef = useRef<HTMLDivElement>(null)
	const merchandisingRef = useRef<HTMLDivElement>(null)
	const locationRef = useRef<HTMLDivElement>(null)
	const photosRef = useRef<HTMLDivElement>(null)
	const pricingDiscountRef = useRef<HTMLDivElement>(null)
	const legalRef = useRef<HTMLDivElement>(null)
	const isAutomaticScroll = useRef(false)

	const [currentStep, setCurrentStep] = useState<ExperienceStepKeyName>(
		ExperienceStepKeyName.enum.experience
	)

	const remappedData =
		experienceQuery.data && remapExperienceForViewOnly(experienceQuery.data)
	const stepsRefMap = useMemo(
		() => ({
			[ExperienceStepKeyName.enum.experience]: experienceRef,
			[ExperienceStepKeyName.enum.background]: backgroundRef,
			[ExperienceStepKeyName.enum.merchandising]: merchandisingRef,
			[ExperienceStepKeyName.enum.location]: locationRef,
			[ExperienceStepKeyName.enum.photos]: photosRef,
			[ExperienceStepKeyName.enum.pricing_discounts]: pricingDiscountRef,
			[ExperienceStepKeyName.enum.legal]: legalRef
		}),
		[]
	)
	const onRemoveAutomaticScroll = debounce(() => {
		isAutomaticScroll.current = false
	}, 700)
	const scrollToSection = (step: ExperienceStepKeyName) => {
		const sectionRef = stepsRefMap[step]
		if (sectionRef?.current) {
			isAutomaticScroll.current = true
			setCurrentStep(step)
			sectionRef.current.scrollIntoView({ behavior: 'smooth' })
			onRemoveAutomaticScroll()
		}
	}

	const intersectionObserver = useMemo(
		() =>
			new IntersectionObserver(
				(entries) => {
					const intersectingEntry = entries.find(
						(entry) => entry.isIntersecting
					)
					if (intersectingEntry) {
						for (const [stepKey, ref] of objectEntries(
							stepsRefMap
						)) {
							if (
								ref.current === intersectingEntry.target &&
								!isAutomaticScroll.current
							) {
								setCurrentStep(stepKey)
							}
						}
					}
				},
				{ threshold: 0.2 }
			),
		[stepsRefMap]
	)

	useEffect(() => {
		for (const ref of Object.values(stepsRefMap)) {
			if (ref.current) {
				intersectionObserver.observe(ref.current)
			}
		}

		return () => {
			for (const ref of Object.values(stepsRefMap)) {
				if (ref.current) {
					intersectionObserver.unobserve(ref.current)
				}
			}
		}
	}, [intersectionObserver, stepsRefMap])

	if (experienceQuery.isPending || !remappedData) {
		return (
			<div className='flex h-full w-full items-center justify-center'>
				<Spinner size={32} />
			</div>
		)
	}

	if (experienceQuery.isError) {
		return (
			<ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
		)
	}

	return (
		<div className='grid h-full grid-cols-[280px_minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden text-neutral-900 [&_aside]:row-[2/-1] [&_header]:col-span-full [&_header]:border-neutral-300 [&_header]:border-b'>
			<Header />

			<StepsNavigation
				currentStep={currentStep}
				onStepClick={scrollToSection}
			/>
			<div className='flex h-full flex-col gap-8 overflow-auto p-8'>
				<ExperienceStep experience={remappedData} ref={experienceRef} />
				<BackgroundStep experience={remappedData} ref={backgroundRef} />
				<MerchandisingStep
					experience={remappedData}
					ref={merchandisingRef}
				/>
				<LocationStep experience={remappedData} ref={locationRef} />
				<PhotosStep experience={remappedData} ref={photosRef} />
				<PricingDiscountStep
					experience={remappedData}
					ref={pricingDiscountRef}
				/>
				<LegalStep experience={remappedData} ref={legalRef} />
			</div>
		</div>
	)
}
